import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

const EMPTY_TILE = null;
const DIFFICULTIES = [3, 4, 5];
const TIMER_INTERVAL_MS = 1000;
const BEST_RESULTS_STORAGE_KEY = 'torahPuzzleBestResultsV1';
const VICTORY_PARTICLE_COUNT = 20;
const VICTORY_ANIMATION_MS = 2400;

const puzzleImageContext = require.context('./PuzzlePics', false, /\.(png|jpe?g|webp|gif|svg)$/i);
const PUZZLE_IMAGES = puzzleImageContext.keys().map((key, index) => {
  const src = puzzleImageContext(key);
  const filename = key.replace('./', '');
  const label = filename
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return {
    id: `${filename}-${index}`,
    src: src.default || src,
    filename,
    label,
  };
});

function getTileCount(gridSize) {
  return gridSize * gridSize;
}

function getSolvedTiles(gridSize) {
  return [...Array(getTileCount(gridSize) - 1).keys(), EMPTY_TILE];
}

function isSolved(tiles, gridSize) {
  const solved = getSolvedTiles(gridSize);
  return tiles.every((tile, index) => tile === solved[index]);
}

function getEmptyIndex(tiles) {
  return tiles.indexOf(EMPTY_TILE);
}

function areAdjacent(a, b, gridSize) {
  const rowA = Math.floor(a / gridSize);
  const colA = a % gridSize;
  const rowB = Math.floor(b / gridSize);
  const colB = b % gridSize;
  return Math.abs(rowA - rowB) + Math.abs(colA - colB) === 1;
}

function moveTile(tiles, tileIndex, gridSize) {
  const emptyIndex = getEmptyIndex(tiles);
  if (!areAdjacent(tileIndex, emptyIndex, gridSize)) return tiles;
  const next = [...tiles];
  [next[tileIndex], next[emptyIndex]] = [next[emptyIndex], next[tileIndex]];
  return next;
}

function countInversions(tiles) {
  const values = tiles.filter((tile) => tile !== EMPTY_TILE);
  let inversions = 0;
  for (let i = 0; i < values.length; i += 1) {
    for (let j = i + 1; j < values.length; j += 1) {
      if (values[i] > values[j]) inversions += 1;
    }
  }
  return inversions;
}

function isSolvable(tiles, gridSize) {
  const inversions = countInversions(tiles);
  if (gridSize % 2 !== 0) return inversions % 2 === 0;
  const emptyIndex = getEmptyIndex(tiles);
  const emptyRowFromTop = Math.floor(emptyIndex / gridSize);
  const emptyRowFromBottom = gridSize - emptyRowFromTop;
  return emptyRowFromBottom % 2 === 0 ? inversions % 2 !== 0 : inversions % 2 === 0;
}

function createShuffledTiles(gridSize) {
  const base = getSolvedTiles(gridSize);
  let shuffled = [...base];
  do {
    shuffled = [...base];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
  } while (!isSolvable(shuffled, gridSize) || isSolved(shuffled, gridSize));
  return shuffled;
}

function getTileBackgroundPosition(tileValue, gridSize) {
  const row = Math.floor(tileValue / gridSize);
  const col = tileValue % gridSize;
  const denom = Math.max(1, gridSize - 1);
  return `${(col / denom) * 100}% ${(row / denom) * 100}%`;
}

function getBoardWidth(gridSize) {
  if (gridSize === 3) return 'min(90vw, 420px)';
  if (gridSize === 4) return 'min(92vw, 500px)';
  return 'min(94vw, 580px)';
}

function getTileNumberSize(gridSize) {
  if (gridSize === 5) return 10;
  if (gridSize === 4) return 11;
  return 12;
}

function formatElapsedTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function getKeyboardTargetIndex(key, emptyIndex, gridSize) {
  const row = Math.floor(emptyIndex / gridSize);
  const col = emptyIndex % gridSize;
  switch (key) {
    case 'ArrowUp':
    case 'w':
    case 'W':
      return row < gridSize - 1 ? emptyIndex + gridSize : -1;
    case 'ArrowDown':
    case 's':
    case 'S':
      return row > 0 ? emptyIndex - gridSize : -1;
    case 'ArrowLeft':
    case 'a':
    case 'A':
      return col < gridSize - 1 ? emptyIndex + 1 : -1;
    case 'ArrowRight':
    case 'd':
    case 'D':
      return col > 0 ? emptyIndex - 1 : -1;
    default:
      return -1;
  }
}

function createDefaultBestResults() {
  return {
    3: { bestMoves: null, bestTime: null },
    4: { bestMoves: null, bestTime: null },
    5: { bestMoves: null, bestTime: null },
  };
}

function readBestResults() {
  if (typeof window === 'undefined') return createDefaultBestResults();
  try {
    const raw = window.localStorage.getItem(BEST_RESULTS_STORAGE_KEY);
    if (!raw) return createDefaultBestResults();
    const parsed = JSON.parse(raw);
    return {
      3: { bestMoves: Number.isFinite(parsed?.[3]?.bestMoves) ? parsed[3].bestMoves : null, bestTime: Number.isFinite(parsed?.[3]?.bestTime) ? parsed[3].bestTime : null },
      4: { bestMoves: Number.isFinite(parsed?.[4]?.bestMoves) ? parsed[4].bestMoves : null, bestTime: Number.isFinite(parsed?.[4]?.bestTime) ? parsed[4].bestTime : null },
      5: { bestMoves: Number.isFinite(parsed?.[5]?.bestMoves) ? parsed[5].bestMoves : null, bestTime: Number.isFinite(parsed?.[5]?.bestTime) ? parsed[5].bestTime : null },
    };
  } catch {
    return createDefaultBestResults();
  }
}

function writeBestResults(results) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BEST_RESULTS_STORAGE_KEY, JSON.stringify(results));
  } catch {
    // Ignore storage failures.
  }
}

function getUpdatedBestResult(currentBest, moves, time) {
  return {
    bestMoves: currentBest?.bestMoves == null || moves < currentBest.bestMoves ? moves : currentBest.bestMoves,
    bestTime: currentBest?.bestTime == null || time < currentBest.bestTime ? time : currentBest.bestTime,
  };
}

function createVictoryParticles(count = VICTORY_PARTICLE_COUNT) {
  return Array.from({ length: count }, (_, index) => ({
    id: `${Date.now()}-${index}`,
    left: Math.random() * 100,
    size: 8 + Math.random() * 12,
    rotation: -40 + Math.random() * 80,
    delay: Math.random() * 0.35,
    duration: 1.5 + Math.random() * 1.1,
    hue: 190 + Math.round(Math.random() * 100),
    tx: `${-80 + Math.random() * 160}px`,
    ty: `${180 + Math.random() * 120}px`,
  }));
}

export default function TorahPuzzle() {
  const [gridSize, setGridSize] = useState(3);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [tiles, setTiles] = useState(() => createShuffledTiles(3));
  const [moves, setMoves] = useState(0);
  const [hasWon, setHasWon] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [canRecordWin, setCanRecordWin] = useState(true);
  const [bestResults, setBestResults] = useState(() => readBestResults());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showVictoryBurst, setShowVictoryBurst] = useState(false);
  const [victoryParticles, setVictoryParticles] = useState([]);

  const audioContextRef = useRef(null);
  const celebrationTimeoutRef = useRef(null);
  const thumbnailStripRef = useRef(null);
  const selectedThumbnailRef = useRef(null);

  const selectedImage = PUZZLE_IMAGES[selectedImageIndex] || null;

  const movableTileSet = useMemo(() => {
    const emptyIndex = getEmptyIndex(tiles);
    const next = new Set();
    tiles.forEach((tile, index) => {
      if (tile !== EMPTY_TILE && areAdjacent(index, emptyIndex, gridSize)) next.add(index);
    });
    return next;
  }, [tiles, gridSize]);

  const activeBest = bestResults[gridSize] || { bestMoves: null, bestTime: null };
  const hasMultipleImages = PUZZLE_IMAGES.length > 1;

  function selectImageByIndex(nextIndex) {
    if (!PUZZLE_IMAGES.length) return;
    const normalizedIndex = (nextIndex + PUZZLE_IMAGES.length) % PUZZLE_IMAGES.length;
    setSelectedImageIndex(normalizedIndex);
    startBoard(gridSize, 'shuffle');
  }

  function goToPreviousImage() {
    selectImageByIndex(selectedImageIndex - 1);
  }

  function goToNextImage() {
    selectImageByIndex(selectedImageIndex + 1);
  }

  function chooseRandomImage() {
    if (PUZZLE_IMAGES.length <= 1) return;

    let nextIndex = selectedImageIndex;
    while (nextIndex === selectedImageIndex) {
      nextIndex = Math.floor(Math.random() * PUZZLE_IMAGES.length);
    }

    selectImageByIndex(nextIndex);
  }

  function getAudioContext() {
    if (typeof window === 'undefined') return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioContextRef.current) audioContextRef.current = new AudioContextClass();
    if (audioContextRef.current.state === 'suspended') audioContextRef.current.resume().catch(() => {});
    return audioContextRef.current;
  }

  function playTone({ frequency, duration = 0.08, delay = 0, type = 'sine', volume = 0.03 }) {
    if (!soundEnabled) return;
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const startTime = ctx.currentTime + delay;
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, startTime);
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.02);
  }

  function playMoveSound() {
    playTone({ frequency: 420, duration: 0.06, type: 'triangle', volume: 0.025 });
  }

  function playVictorySound() {
    [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
      playTone({
        frequency,
        duration: index === 3 ? 0.34 : 0.18,
        delay: index * 0.13,
        type: index % 2 === 0 ? 'triangle' : 'sine',
        volume: 0.04,
      });
    });
  }

  function triggerVictoryCelebration() {
    setVictoryParticles(createVictoryParticles());
    setShowVictoryBurst(true);
    playVictorySound();
    if (celebrationTimeoutRef.current) window.clearTimeout(celebrationTimeoutRef.current);
    celebrationTimeoutRef.current = window.setTimeout(() => {
      setShowVictoryBurst(false);
      setVictoryParticles([]);
    }, VICTORY_ANIMATION_MS);
  }

  function startBoard(nextGridSize, mode = 'shuffle') {
    setGridSize(nextGridSize);
    setMoves(0);
    setElapsedSeconds(0);
    setShowVictoryBurst(false);
    setVictoryParticles([]);

    if (mode === 'solved') {
      setTiles(getSolvedTiles(nextGridSize));
      setHasWon(true);
      setIsTimerRunning(false);
      setCanRecordWin(false);
      return;
    }

    setTiles(createShuffledTiles(nextGridSize));
    setHasWon(false);
    setIsTimerRunning(true);
    setCanRecordWin(true);
  }

  function handleTileMove(index) {
    const nextTiles = moveTile(tiles, index, gridSize);
    if (nextTiles === tiles) return;
    playMoveSound();
    setTiles(nextTiles);
    setMoves((prev) => prev + 1);
  }

  useEffect(() => {
    if (!isSolved(tiles, gridSize) || hasWon) return;
    setHasWon(true);
    setIsTimerRunning(false);
    triggerVictoryCelebration();
    if (!canRecordWin) return;
    setBestResults((prev) => {
      const next = { ...prev, [gridSize]: getUpdatedBestResult(prev[gridSize], moves, elapsedSeconds) };
      writeBestResults(next);
      return next;
    });
  }, [tiles, gridSize, hasWon, canRecordWin, moves, elapsedSeconds]);

  useEffect(() => {
    if (!isTimerRunning) return undefined;
    const timerId = window.setInterval(() => setElapsedSeconds((prev) => prev + 1), TIMER_INTERVAL_MS);
    return () => window.clearInterval(timerId);
  }, [isTimerRunning]);

  useEffect(() => {
    function handleKeyDown(event) {
      const activeTag = document.activeElement?.tagName;
      const isTypingField = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT';
      if (isTypingField) return;

      if (hasMultipleImages && (event.key === '[' || event.key === 'PageUp')) {
        event.preventDefault();
        goToPreviousImage();
        return;
      }

      if (hasMultipleImages && (event.key === ']' || event.key === 'PageDown')) {
        event.preventDefault();
        goToNextImage();
        return;
      }

      if (hasMultipleImages && (event.key === 'r' || event.key === 'R')) {
        event.preventDefault();
        chooseRandomImage();
        return;
      }

      if (hasWon) return;

      const targetIndex = getKeyboardTargetIndex(event.key, getEmptyIndex(tiles), gridSize);
      if (targetIndex < 0) return;
      event.preventDefault();
      handleTileMove(targetIndex);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tiles, gridSize, hasWon, hasMultipleImages, selectedImageIndex]);

  useEffect(() => () => {
    if (celebrationTimeoutRef.current) window.clearTimeout(celebrationTimeoutRef.current);
  }, []);

  useEffect(() => {
    const stripEl = thumbnailStripRef.current;
    const thumbnailEl = selectedThumbnailRef.current;
    if (!stripEl || !thumbnailEl) return;

    const stripRect = stripEl.getBoundingClientRect();
    const thumbRect = thumbnailEl.getBoundingClientRect();
    const currentScroll = stripEl.scrollLeft;
    const thumbCenterWithinStrip = thumbRect.left - stripRect.left + currentScroll + thumbRect.width / 2;
    const targetScrollLeft = thumbCenterWithinStrip - stripRect.width / 2;

    stripEl.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: 'smooth',
    });
  }, [selectedImageIndex]);

  if (!selectedImage) {
    return (
      <div className="mainInner puzzlePage">
        <div className="puzzleHeader">
          <div className="puzzleHeaderSpacer" />
          <h2 className="puzzleTitle">Torah Puzzle</h2>
          <div className="puzzleHeaderAction">
            <Link to="/Game" className="puzzleBackButton">Back to Games</Link>
          </div>
        </div>
        <div className="heroCard" style={{ gridTemplateColumns: '1fr', maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontWeight: 900, color: '#b00020' }}>
            No puzzle images were found in <code>src/PuzzlePics</code>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainInner puzzlePage">
      <style>{`
        @keyframes puzzleVictoryPulse {
          0% { transform: scale(1); box-shadow: 0 0 0 rgba(31,92,255,0.18); }
          100% { transform: scale(1.02); box-shadow: 0 18px 42px rgba(31,92,255,0.28); }
        }
        @keyframes puzzleConfettiBurst {
          0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate3d(var(--tx), var(--ty), 0) rotate(var(--rot)); opacity: 0; }
        }
      `}</style>

      <div className="puzzleHeader">
        <div className="puzzleHeaderSpacer" />
        <h2 className="puzzleTitle">Torah Puzzle</h2>
        <div className="puzzleHeaderAction">
          <Link to="/Game" className="puzzleBackButton">Back to Games</Link>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div className="heroCard" style={{ gridTemplateColumns: '1fr', maxWidth: 980, justifyItems: 'center', textAlign: 'center', gap: 16 }}>
          <div style={{ color: '#0b1b2a', opacity: 0.92, maxWidth: 720, lineHeight: 1.5 }}>
            Choose an image, choose your difficulty, then slide the tiles until the picture is restored.
          </div>

          <div style={{ color: '#0b1b2a', opacity: 0.82, maxWidth: 720, lineHeight: 1.5, fontSize: 14 }}>
            Keyboard controls: use <b>arrow keys</b> or <b>W A S D</b> to move tiles beside the empty space. Use <b>[</b> and <b>]</b> or <b>Page Up</b> and <b>Page Down</b> to cycle pictures, and <b>R</b> for a random picture.
          </div>

          <div style={{ width: '100%', maxWidth: 860 }}>
            <div style={{ fontWeight: 900, color: '#0b1b2a', marginBottom: 10 }}>Choose Picture</div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={goToPreviousImage}
                disabled={!hasMultipleImages}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 999,
                  border: '1px solid rgba(11,27,42,0.16)',
                  background: hasMultipleImages ? 'white' : 'rgba(255,255,255,0.55)',
                  color: '#0b1b2a',
                  fontSize: 24,
                  fontWeight: 900,
                  cursor: hasMultipleImages ? 'pointer' : 'not-allowed',
                  boxShadow: hasMultipleImages ? '0 8px 18px rgba(0,0,0,0.08)' : 'none',
                }}
                aria-label="Previous picture"
                title="Previous picture"
              >
                ‹
              </button>

              <div style={{ display: 'grid', justifyItems: 'center', gap: 12, minWidth: 0, flex: '1 1 320px', maxWidth: 520 }}>
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 30,
                    color: '#0b1b2a',
                    lineHeight: 1.1,
                    textAlign: 'center',
                  }}
                >
                  {selectedImage.label}
                </div>

                <button
                  onClick={() => startBoard(gridSize, 'shuffle')}
                  style={{
                    border: '2px solid #0070f3',
                    borderRadius: 18,
                    background: 'rgba(0,112,243,0.08)',
                    padding: 10,
                    cursor: 'pointer',
                    boxShadow: '0 10px 24px rgba(0,112,243,0.18)',
                  }}
                  title={selectedImage.label}
                >
                  <img
                    key={selectedImage.id}
                    src={selectedImage.src}
                    alt={selectedImage.label}
                    style={{
                      width: 'min(280px, 72vw)',
                      aspectRatio: '1 / 1',
                      objectFit: 'cover',
                      borderRadius: 12,
                      display: 'block',
                      transition: 'transform 0.28s ease, opacity 0.28s ease, filter 0.28s ease',
                      animation: 'puzzleVictoryPulse 0.45s ease-out 1',
                    }}
                  />
                </button>

                <div style={{ fontSize: 12, fontWeight: 700, color: '#0b1b2a', opacity: 0.7 }}>
                  {selectedImageIndex + 1} of {PUZZLE_IMAGES.length}
                </div>
              </div>

              <button
                onClick={goToNextImage}
                disabled={!hasMultipleImages}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 999,
                  border: '1px solid rgba(11,27,42,0.16)',
                  background: hasMultipleImages ? 'white' : 'rgba(255,255,255,0.55)',
                  color: '#0b1b2a',
                  fontSize: 24,
                  fontWeight: 900,
                  cursor: hasMultipleImages ? 'pointer' : 'not-allowed',
                  boxShadow: hasMultipleImages ? '0 8px 18px rgba(0,0,0,0.08)' : 'none',
                }}
                aria-label="Next picture"
                title="Next picture"
              >
                ›
              </button>
            </div>

            <div
              ref={thumbnailStripRef}
              style={{
                display: 'flex',
                gap: 10,
                overflowX: 'auto',
                justifyContent: 'flex-start',
                paddingTop: 14,
                paddingBottom: 4,
                scrollBehavior: 'smooth',
                scrollSnapType: 'x proximity',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {PUZZLE_IMAGES.map((image, index) => {
                const isActive = index === selectedImageIndex;
                return (
                  <button
                    key={image.id}
                    ref={isActive ? selectedThumbnailRef : null}
                    onClick={() => selectImageByIndex(index)}
                    style={{
                      flex: '0 0 auto',
                      padding: 6,
                      borderRadius: 14,
                      border: isActive ? '2px solid #0070f3' : '1px solid rgba(11,27,42,0.12)',
                      background: isActive ? 'rgba(0,112,243,0.08)' : 'rgba(255,255,255,0.8)',
                      cursor: 'pointer',
                      boxShadow: isActive ? '0 10px 24px rgba(0,112,243,0.18)' : '0 8px 18px rgba(0,0,0,0.08)',
                      transform: isActive ? 'translateY(-2px) scale(1.04)' : 'scale(1)',
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, background 0.25s ease',
                      scrollSnapAlign: 'center',
                    }}
                    title={image.label}
                    aria-label={`Choose picture ${image.label}`}
                  >
                    <img
                      src={image.src}
                      alt={image.label}
                      style={{
                        width: 74,
                        height: 74,
                        objectFit: 'cover',
                        borderRadius: 10,
                        display: 'block',
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ fontWeight: 900, color: '#0b1b2a' }}>Difficulty:</div>
            {DIFFICULTIES.map((size) => {
              const isActive = size === gridSize;
              return (
                <button
                  key={size}
                  onClick={() => startBoard(size, 'shuffle')}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 999,
                    border: isActive ? '1px solid #1f3b73' : '1px solid rgba(11,27,42,0.18)',
                    background: isActive ? '#0070f3' : 'white',
                    color: isActive ? 'white' : '#0b1b2a',
                    fontWeight: 900,
                    cursor: 'pointer',
                    boxShadow: isActive ? '0 8px 20px rgba(0,112,243,0.28)' : 'none',
                  }}
                >
                  {size}x{size}
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <button onClick={() => startBoard(gridSize, 'shuffle')} style={{ padding: '10px 16px', borderRadius: 999, border: '1px solid #1f3b73', background: '#0070f3', color: 'white', fontWeight: 900, cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,112,243,0.28)' }}>
              Shuffle
            </button>
            <button
              onClick={chooseRandomImage}
              disabled={!hasMultipleImages}
              style={{
                padding: '10px 16px',
                borderRadius: 999,
                border: '1px solid rgba(11,27,42,0.18)',
                background: hasMultipleImages ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.55)',
                color: '#0b1b2a',
                fontWeight: 900,
                cursor: hasMultipleImages ? 'pointer' : 'not-allowed',
              }}
            >
              Random Picture
            </button>
            <button onClick={() => startBoard(gridSize, 'solved')} style={{ padding: '10px 16px', borderRadius: 999, border: '1px solid rgba(11,27,42,0.18)', background: 'white', color: '#0b1b2a', fontWeight: 900, cursor: 'pointer' }}>
              Show Solved Board
            </button>
            <button
              onClick={() => setSoundEnabled((prev) => !prev)}
              style={{ padding: '10px 16px', borderRadius: 999, border: '1px solid rgba(11,27,42,0.18)', background: soundEnabled ? 'rgba(0,112,243,0.08)' : 'white', color: '#0b1b2a', fontWeight: 900, cursor: 'pointer' }}
            >
              Sound: {soundEnabled ? 'On' : 'Off'}
            </button>
            <div style={{ padding: '10px 14px', borderRadius: 999, background: 'rgba(0,112,243,0.08)', border: '1px solid rgba(0,112,243,0.18)', color: '#0b1b2a', fontWeight: 900 }}>Moves: {moves}</div>
            <div style={{ padding: '10px 14px', borderRadius: 999, background: 'rgba(11,27,42,0.06)', border: '1px solid rgba(11,27,42,0.12)', color: '#0b1b2a', fontWeight: 900 }}>Board: {gridSize}x{gridSize}</div>
            <div style={{ padding: '10px 14px', borderRadius: 999, background: 'rgba(11,107,47,0.08)', border: '1px solid rgba(11,107,47,0.18)', color: '#0b1b2a', fontWeight: 900 }}>Time: {formatElapsedTime(elapsedSeconds)}</div>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ padding: '10px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.75)', border: '1px solid rgba(11,27,42,0.12)', color: '#0b1b2a', fontWeight: 900 }}>
              Best Moves ({gridSize}x{gridSize}): {activeBest.bestMoves == null ? '—' : activeBest.bestMoves}
            </div>
            <div style={{ padding: '10px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.75)', border: '1px solid rgba(11,27,42,0.12)', color: '#0b1b2a', fontWeight: 900 }}>
              Best Time ({gridSize}x{gridSize}): {activeBest.bestTime == null ? '—' : formatElapsedTime(activeBest.bestTime)}
            </div>
            <div style={{ padding: '10px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.75)', border: '1px solid rgba(11,27,42,0.12)', color: '#0b1b2a', fontWeight: 900 }}>
              Picture: {selectedImage.label}
            </div>
          </div>

          {hasWon ? (
            <div style={{ padding: '12px 16px', borderRadius: 14, background: 'rgba(11,107,47,0.08)', border: '1px solid rgba(11,107,47,0.2)', color: '#0b6b2f', fontWeight: 900, animation: 'puzzleVictoryPulse 0.9s ease-in-out infinite alternate' }}>
              Puzzle solved on {gridSize}x{gridSize} in {moves} move{moves === 1 ? '' : 's'} and {formatElapsedTime(elapsedSeconds)}.
            </div>
          ) : null}

          <div style={{ position: 'relative', width: getBoardWidth(gridSize) }}>
            {showVictoryBurst ? (
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible', zIndex: 3 }} aria-hidden="true">
                {victoryParticles.map((particle) => (
                  <span
                    key={particle.id}
                    style={{
                      position: 'absolute',
                      left: `${particle.left}%`,
                      top: '18%',
                      width: particle.size,
                      height: particle.size * 1.6,
                      borderRadius: 6,
                      background: `linear-gradient(180deg, hsla(${particle.hue}, 95%, 72%, 1), hsla(${particle.hue - 35}, 95%, 56%, 1))`,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.14)',
                      opacity: 0,
                      ['--tx']: particle.tx,
                      ['--ty']: particle.ty,
                      ['--rot']: `${particle.rotation}deg`,
                      animation: `puzzleConfettiBurst ${particle.duration}s ease-out ${particle.delay}s forwards`,
                    }}
                  />
                ))}
              </div>
            ) : null}

            <div style={{ width: '100%', aspectRatio: '1 / 1', display: 'grid', gridTemplateColumns: `repeat(${gridSize}, 1fr)`, gap: 6, padding: 6, borderRadius: 18, background: 'rgba(11,27,42,0.08)', border: '1px solid rgba(11,27,42,0.12)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)' }}>
              {tiles.map((tile, index) => {
                if (tile === EMPTY_TILE) {
                  return <div key={`empty-${index}`} aria-label="Empty tile space" style={{ borderRadius: 12, background: 'rgba(11,27,42,0.12)', border: '1px dashed rgba(11,27,42,0.18)' }} />;
                }

                const isMovable = movableTileSet.has(index);
                return (
                  <button
                    key={`${tile}-${index}`}
                    onClick={() => handleTileMove(index)}
                    aria-label={`Move tile ${tile + 1}`}
                    style={{
                      border: isMovable ? '1px solid rgba(0,112,243,0.34)' : '1px solid rgba(11,27,42,0.14)',
                      borderRadius: 12,
                      cursor: isMovable ? 'pointer' : 'not-allowed',
                      backgroundImage: `url(${selectedImage.src})`,
                      backgroundSize: `${gridSize * 100}% ${gridSize * 100}%`,
                      backgroundPosition: getTileBackgroundPosition(tile, gridSize),
                      backgroundRepeat: 'no-repeat',
                      backgroundColor: '#dff6ff',
                      boxShadow: isMovable ? '0 8px 20px rgba(0,112,243,0.18)' : '0 6px 18px rgba(0,0,0,0.12)',
                      position: 'relative',
                      overflow: 'hidden',
                      transform: isMovable ? 'translateY(-1px)' : 'none',
                    }}
                    title={isMovable ? 'Move tile' : 'Tile must be next to the empty space'}
                  >
                    <span style={{ position: 'absolute', bottom: 6, right: 8, minWidth: gridSize === 5 ? 20 : 22, height: gridSize === 5 ? 20 : 22, borderRadius: 999, background: 'rgba(255,255,255,0.92)', color: '#0b1b2a', fontSize: getTileNumberSize(gridSize), fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                      {tile + 1}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}